// Canonical date utilities for the Multi-Car module.
// All parsing goes through the shared parseDateTime (services/possessionMatcher),
// which returns *local* Dates; formatting is local as well, so a value never
// shifts between parse and print.
import { parseDateTime, formatDateTime } from '../../../services/possessionMatcher';

/**
 * Accept the dash-separated / two-digit-year spellings the sheets sometimes
 * use ("03-07-2024 16:28", "3/7/24") by rewriting them to the canonical
 * day-first "DD/MM/YYYY HH:mm" before handing off to parseDateTime.
 */
const canonicalize = (text) => {
    const m = /^(\d{1,2})[-/](\d{1,2})[-/](\d{2}|\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/.exec(text);
    if (!m) return text;
    const [, d, mo, y, h, mi, s] = m;
    const year = y.length === 2 ? String(2000 + parseInt(y, 10)) : y;
    const time = h ? ` ${h}:${mi}${s ? `:${s}` : ''}` : '';
    return `${d}/${mo}/${year}${time}`;
};

const parseAny = (value) => {
    const direct = parseDateTime(value);
    if (direct) return direct;
    if (typeof value !== 'string') return null;
    const text = value.trim();
    if (!text) return null;
    const canon = canonicalize(text);
    return canon === text ? null : parseDateTime(canon);
};

/** Dates coming from a parsed sheet ("Revenue Date"): Date objects, ISO or day-first strings. */
export const parseRevenueDate = (value) => parseAny(value);

/** "DD/MM/YYYY HH:mm" in local time; 'Invalid Date' for anything that is not a valid Date. */
export const formatDateDDMMYYYY = (date) => {
    if (!(date instanceof Date) || isNaN(date)) {
        return 'Invalid Date';
    }
    return formatDateTime(date);
};

export const formatDateForDisplay = (dateStr) => {
    if (!dateStr || dateStr.toString().trim() === '') return '';

    // Handle different date formats and remove time
    let cleanDate = dateStr.toString();

    // Remove time part if it exists (format: DD-MM-YYYYHHMM or DD/MM/YYYYHHMM)
    const dateTimeMatch = cleanDate.match(/^(\d{1,2}[-/]\d{1,2}[-/]\d{4})(\d{4})?/);
    if (dateTimeMatch) {
        cleanDate = dateTimeMatch[1]; // Get only the date part
    } else {
        // Handle space-separated date and time
        cleanDate = cleanDate.split(' ')[0];
    }

    // Convert all dates to DD/MM/YYYY format
    if (cleanDate.includes('-')) {
        return cleanDate.replace(/-/g, '/');
    }

    return cleanDate;
};

/** Sheet cell -> local Date (day-first, ISO, Excel serial, Date object) or null. */
export const parseSheetDate = (value) => parseAny(value);
